import { useState, useEffect } from 'react';
import { employeeController } from '../../controllers/employeeController';
import Employee from '../../models/Employee';
import { Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, ChevronUp, ChevronDown, Users, UserCheck, UserPlus, UserX } from 'lucide-react';

const EmployeesPage = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterDepartment, setFilterDepartment] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', dateOfBirth: '',
    gender: 'Male', address: '', department: '', designation: '',
    joiningDate: '', employmentType: 'Full-time', status: 'Active', salary: 0
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => { fetchEmployees(); }, []);

  useEffect(() => {
    let result = [...employees];
    if (searchQuery) {
      result = result.filter(e =>
        e.getFullName().toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.employeeId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.department.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (filterStatus !== 'All') result = result.filter(e => e.status === filterStatus);
    if (filterDepartment !== 'All') result = result.filter(e => e.department === filterDepartment);
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (sortField === 'fullName') { aVal = a.getFullName(); bVal = b.getFullName(); }
      if (typeof aVal === 'string') return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    setFilteredEmployees(result);
    setCurrentPage(1);
  }, [employees, searchQuery, filterStatus, filterDepartment, sortField, sortDirection]);

  const fetchEmployees = async () => {
    setLoading(true);
    const result = await employeeController.getAll();
    if (result.success) setEmployees(result.data);
    else alert('Error: ' + result.message);
    setLoading(false);
  };

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors([]);
  };

  const handleAdd = () => {
    setFormData({ firstName: '', lastName: '', email: '', phone: '', dateOfBirth: '', gender: 'Male', address: '', department: '', designation: '', joiningDate: '', employmentType: 'Full-time', status: 'Active', salary: 0 });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    const result = await employeeController.create(formData);
    if (result.success) { setShowAddModal(false); fetchEmployees(); alert('✅ Employee created!'); }
    else setFormErrors(result.errors || [result.message]);
    setFormSubmitting(false);
  };

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      firstName: employee.firstName, lastName: employee.lastName, email: employee.email,
      phone: employee.phone, dateOfBirth: employee.dateOfBirth?.split('T')[0] || '',
      gender: employee.gender, address: employee.address, department: employee.department,
      designation: employee.designation, joiningDate: employee.joiningDate?.split('T')[0] || '',
      employmentType: employee.employmentType, status: employee.status, salary: employee.salary
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    const result = await employeeController.update(selectedEmployee.id, formData);
    if (result.success) { setShowEditModal(false); fetchEmployees(); alert('✅ Employee updated!'); }
    else setFormErrors(result.errors || [result.message]);
    setFormSubmitting(false);
  };

  const handleView = (employee) => { setSelectedEmployee(employee); setShowViewModal(true); };

  const handleDelete = async (employee) => {
    try {
      const isConfirmed = window.confirm(
        `Delete employee ${employee.getFullName()}?`
      );
  
      if (!isConfirmed) return;
  
      const result = await employeeController.delete(
        employee.id || employee._id
      );
  
      if (result?.success) {
        await fetchEmployees();
        alert('✅ Employee deleted!');
      } else {
        alert(result?.message || '❌ Failed to delete employee');
      }
    } catch (error) {
      console.error('Delete employee error:', error);
      alert('❌ Something went wrong while deleting employee');
    }
  };
  

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const handleExport = async () => {
    const result = await employeeController.export({ status: filterStatus }, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employees-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Employees exported!');
    } else alert('Error: ' + result.message);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  const stats = {
    total: employees.length,
    active: employees.filter(e => e.status === 'Active').length,
    newThisMonth: employees.filter(e => {
      const joined = new Date(e.joiningDate);
      const now = new Date();
      return joined.getMonth() === now.getMonth() && joined.getFullYear() === now.getFullYear();
    }).length,
    onLeave: employees.filter(e => e.status === 'On Leave').length
  };

  const departments = [...new Set(employees.map(e => e.department))].filter(Boolean);

  const styles = {
    container: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' },
    secondaryButton: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' },
    select: { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' },
    td: { padding: '16px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
    actionButton: { padding: '6px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', transition: 'background-color 0.2s' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #e2e8f0' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' },
    errorList: { backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' },
    errorItem: { color: '#dc2626', fontSize: '14px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' },
    modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Employee Management</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={handleExport}>
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} /> Add Employee
          </button>
        </div>
      </div>

      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Employees</div>
            <Users size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Active</div>
            <UserCheck size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.active}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>New This Month</div>
            <UserPlus size={24} color="#3b82f6" />
          </div>
          <div style={{ ...styles.statValue, color: '#3b82f6' }}>{stats.newThisMonth}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>On Leave</div>
            <UserX size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.onLeave}</div>
        </div>
      </div>

      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input type="text" placeholder="Search employees..." style={styles.searchInput} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <select style={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="All">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="On Leave">On Leave</option>
        </select>
        <select style={styles.select} value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)}>
          <option value="All">All Departments</option>
          {departments.map(dept => <option key={dept} value={dept}>{dept}</option>)}
        </select>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading employees...</div>
        ) : currentEmployees.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No employees found. Click "Add Employee" to create one.</div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th} onClick={() => handleSort('employeeId')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Employee ID {sortField === 'employeeId' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th} onClick={() => handleSort('fullName')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Name {sortField === 'fullName' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th} onClick={() => handleSort('department')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Department {sortField === 'department' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Designation</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentEmployees.map((emp) => (
                  <tr key={emp.id} style={{ cursor: 'pointer' }}>
                    <td style={styles.td}>
                      <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>{emp.employeeId}</div>
                    </td>
                    <td style={styles.td} onClick={() => handleView(emp)}>
                      <div style={{ fontWeight: '500' }}>{emp.getFullName()}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{emp.designation}</div>
                    </td>
                    <td style={styles.td}>{emp.email}</td>
                    <td style={styles.td}>{emp.phone}</td>
                    <td style={styles.td}>{emp.department}</td>
                    <td style={styles.td}>{emp.designation}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, backgroundColor: `${emp.getStatusColor()}20`, color: emp.getStatusColor() }}>
                        {emp.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleView(emp); }} title="View">
                          <Eye size={18} color="#64748b" />
                        </button>
                        <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleEdit(emp); }} title="Edit">
                          <Edit2 size={18} color="#3b82f6" />
                        </button>
                        <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleDelete(emp); }} title="Delete">
                          <Trash2 size={18} color="#ef4444" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredEmployees.length)} of {filteredEmployees.length} employees
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{showAddModal ? 'Add New Employee' : 'Edit Employee'}</h2>
              <button style={{ ...styles.actionButton, padding: '8px' }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>First Name *</label>
                    <input type="text" name="firstName" style={styles.input} value={formData.firstName} onChange={handleInputChange} required />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Last Name *</label>
                    <input type="text" name="lastName" style={styles.input} value={formData.lastName} onChange={handleInputChange} required />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Email *</label>
                    <input type="email" name="email" style={styles.input} value={formData.email} onChange={handleInputChange} required />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Phone *</label>
                    <input type="tel" name="phone" style={styles.input} value={formData.phone} onChange={handleInputChange} required />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Date of Birth</label>
                    <input type="date" name="dateOfBirth" style={styles.input} value={formData.dateOfBirth} onChange={handleInputChange} />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Gender</label>
                    <select name="gender" style={styles.input} value={formData.gender} onChange={handleInputChange}>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Department *</label>
                    <select name="department" style={styles.input} value={formData.department} onChange={handleInputChange} required>
                      <option value="">Select Department</option>
                      <option value="IT">IT</option>
                      <option value="HR">HR</option>
                      <option value="Finance">Finance</option>
                      <option value="Sales">Sales</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Operations">Operations</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Designation *</label>
                    <input type="text" name="designation" style={styles.input} value={formData.designation} onChange={handleInputChange} required />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Joining Date *</label>
                    <input type="date" name="joiningDate" style={styles.input} value={formData.joiningDate} onChange={handleInputChange} required />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Employment Type</label>
                    <select name="employmentType" style={styles.input} value={formData.employmentType} onChange={handleInputChange}>
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Intern">Intern</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select name="status" style={styles.input} value={formData.status} onChange={handleInputChange}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="On Leave">On Leave</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Salary (₹)</label>
                    <input type="number" name="salary" style={styles.input} value={formData.salary} onChange={handleInputChange} min="0" />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Address</label>
                  <input type="text" name="address" style={styles.input} value={formData.address} onChange={handleInputChange} />
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button type="button" style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }} disabled={formSubmitting}>
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>
                  {formSubmitting ? 'Saving...' : (showAddModal ? 'Create Employee' : 'Update Employee')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedEmployee && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Employee Details</h2>
              <button style={{ ...styles.actionButton, padding: '8px' }} onClick={() => setShowViewModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Employee ID</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedEmployee.employeeId}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Full Name</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedEmployee.getFullName()}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Email</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedEmployee.email}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Phone</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedEmployee.phone}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Department</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedEmployee.department}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Designation</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedEmployee.designation}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Joining Date</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedEmployee.joiningDate ? new Date(selectedEmployee.joiningDate).toLocaleDateString() : 'N/A'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Employment Type</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedEmployee.employmentType}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span style={{ ...styles.badge, backgroundColor: `${selectedEmployee.getStatusColor()}20`, color: selectedEmployee.getStatusColor() }}>
                    {selectedEmployee.status}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Salary</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>₹{selectedEmployee.salary.toLocaleString()}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Gender</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedEmployee.gender || 'N/A'}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Date of Birth</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedEmployee.dateOfBirth ? new Date(selectedEmployee.dateOfBirth).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
              </div>

              {selectedEmployee.address && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Address</div>
                  <div style={{ fontSize: '14px', color: '#1e293b', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                    {selectedEmployee.address}
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowViewModal(false); handleEdit(selectedEmployee); }}>
                <Edit2 size={16} /> Edit Employee
              </button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesPage;